// frontend/src/pages/ClientsPage.js
// Project:      Vision ANPR by Castle Traffic Ltd - Automatic Number Plate Recognition
// Author:       Leon du Plooy - Castle Traffic Ltd - Copyright 2025
// Contact:      dev@castle-traffic.co.uk
// Created:      2025-07-10
// Updated:      2026-09-25 10:45 BST
//
// Desktop resize regression fix (2026-09-25):
//   - Removed the viewport-derived ReactGridLayout `maxRows` ceiling.
//   - Desktop widgets may now grow vertically beyond the initial viewport and the page scrolls naturally.
//   - Layout persistence preserves x/y/w/h while preventing negative y positions.
//   - Page-owned widget positioning is no longer forced back inside viewport rows.
//   - Existing mobile single-column presentation and mobile sizing behaviour are unchanged.
//
// Description:  Configurable Client Manager surface using the shared registry-backed widget
//               framework while retaining page-owned customer/contact state and modal workflows.
//
// Mobile Responsiveness (2026-09-04):
//   - Added a dedicated mobile presentation at <= 768px.
//   - Mobile renders configured Client Manager widgets in a natural single-column flow.
//   - Mobile ordering follows the persisted desktop layout position (y, then x).
//   - react-grid-layout drag/resize behaviour remains desktop-only.
//   - Mobile rendering never persists layout changes, so viewing Client Manager on a phone
//     cannot alter the user's desktop widget arrangement.
//   - Reflowed the page heading and Add Widget action for compact mobile widths.
//   - Reduced mobile page gutters while preserving registry-backed widgets, page-owned
//     customer/contact modal workflows, localStorage keys, and desktop layout behaviour.
//
// Change Summary (2026-07-30):
//   - Removed all direct widget component imports and the page-specific resolver switch.
//   - Replaced stored React elements with canonical widget-id state.
//   - Added surface-filtered Add Widget behaviour for the `clients` presentation surface.
//   - Added shared WidgetRenderer, WidgetSurface, and WidgetMigration integration.
//   - Preserved customer/contact editing callbacks and scroll-safe WidgetWrapper rendering.
//   - Added deterministic storage validation, migration, persistence, and layout de-duplication.
//
// Operational Notes:
//   - Business state and widget-specific props remain owned by this page.
//   - Widget components and metadata are resolved exclusively through WidgetRegistry.js.
//   - The Clients picker can only offer widgets registered with dashboards: ["clients"].
//   - Existing localStorage keys are retained to avoid resetting user layouts.

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import GridLayout from "react-grid-layout";
import "react-grid-layout/css/styles.css";
import "react-resizable/css/styles.css";

import AddWidgetModal from "../components/AddWidgetModal";
import ManageUserModal from "../components/ManageUserModal";
import EditCustomerModal from "../components/EditCustomerModal";
import EditContactModal from "../components/EditContactModal";
import CreateContactModal from "../components/CreateContactModal";

import WidgetRenderer from "../widgets/WidgetRenderer.jsx";
import { migrateStoredWidgetState, migrateWidgetId } from "../widgets/WidgetMigration";
import {
  createSurfaceLayoutItem,
  getSurfaceWidget,
  getSurfaceWidgetIds,
  isWidgetAvailableOnSurface,
} from "../widgets/WidgetSurface";
import { useTheme } from "../context/ThemeContext.jsx";

const SURFACE = "clients";
const STORAGE_KEY_LAYOUT = "clientsLayout";
const STORAGE_KEY_WIDGETS = "clientsWidgets";

const MOBILE_BREAKPOINT = 768;

function useIsMobile(breakpoint = MOBILE_BREAKPOINT) {
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window === "undefined" || !window.matchMedia) {
      return false;
    }

    return window.matchMedia(`(max-width: ${breakpoint}px)`).matches;
  });

  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) {
      return undefined;
    }

    const mediaQuery = window.matchMedia(`(max-width: ${breakpoint}px)`);

    const apply = () => {
      setIsMobile(mediaQuery.matches);
    };

    apply();

    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener("change", apply);
    } else {
      mediaQuery.addListener(apply);
    }

    return () => {
      if (mediaQuery.removeEventListener) {
        mediaQuery.removeEventListener("change", apply);
      } else {
        mediaQuery.removeListener(apply);
      }
    };
  }, [breakpoint]);

  return isMobile;
}

const defaultLayout = [
  createSurfaceLayoutItem(SURFACE, "job_list", {
    x: 0,
    y: 0,
    w: 6,
    h: 5,
    minW: 4,
    minH: 4,
    maxW: 12,
  }),
];

const normaliseLayout = (layout) =>
  (Array.isArray(layout) ? layout : []).map((item) => {
    const currentY = Number.isFinite(item?.y) ? item.y : 0;
    return { ...item, y: Math.max(0, currentY) };
  });

const isValidStoredState = (layout, widgetIds) => {
  if (!Array.isArray(layout) || !Array.isArray(widgetIds)) return false;
  if (widgetIds.some((widgetId) => typeof widgetId !== "string")) return false;
  if (layout.some((item) => !item || typeof item !== "object" || typeof item.i !== "string")) return false;

  const layoutIds = new Set(layout.map((item) => migrateWidgetId(item.i)));
  return widgetIds.every((widgetId) => layoutIds.has(migrateWidgetId(widgetId)));
};

const ClientsPage = () => {
  const { theme } = useTheme();
  const isMobile = useIsMobile();
  const [widgetIds, setWidgetIds] = useState([]);
  const [layout, setLayout] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [gridWidth, setGridWidth] = useState(1200);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [editingContact, setEditingContact] = useState(null);
  const [showContactModal, setShowContactModal] = useState(false);
  const [newCustomerId, setNewCustomerId] = useState(null);
  const gridRef = useRef(null);

  const containerPadding = 24;
  const headerHeight = 64;
  const rowHeight = 40;
  const persistState = useCallback((nextLayout, nextWidgetIds) => {
    try {
      localStorage.setItem(STORAGE_KEY_LAYOUT, JSON.stringify(nextLayout));
      localStorage.setItem(STORAGE_KEY_WIDGETS, JSON.stringify(nextWidgetIds));
    } catch (error) {
      console.warn("Unable to persist Clients widget state", error);
    }
  }, []);

  useEffect(() => {
    const node = gridRef.current;
    if (!node) return undefined;

    const updateWidth = () => {
      const width = Math.floor(node.getBoundingClientRect().width || node.offsetWidth || 1200);
      if (width > 0) setGridWidth(width);
    };

    updateWidth();
    const observer = new ResizeObserver(updateWidth);
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    try {
      const stored = migrateStoredWidgetState({
        layoutKey: STORAGE_KEY_LAYOUT,
        widgetsKey: STORAGE_KEY_WIDGETS,
      });

      if (isValidStoredState(stored.layout, stored.widgetIds)) {
        const validWidgetIds = stored.widgetIds.filter((widgetId) =>
          isWidgetAvailableOnSurface(SURFACE, widgetId)
        );
        const validIdSet = new Set(validWidgetIds);
        const validLayout = normaliseLayout(
          stored.layout.filter((item) => validIdSet.has(migrateWidgetId(item.i)))
        );

        if (validWidgetIds.length > 0 && validLayout.length > 0) {
          setWidgetIds(validWidgetIds);
          setLayout(validLayout);
          persistState(validLayout, validWidgetIds);
          return;
        }
      }
    } catch (error) {
      console.warn("Clients widget storage was invalid and has been reset", error);
      localStorage.removeItem(STORAGE_KEY_LAYOUT);
      localStorage.removeItem(STORAGE_KEY_WIDGETS);
    }

    const initialWidgetIds = defaultLayout.map((item) => item.i);
    setLayout(defaultLayout);
    setWidgetIds(initialWidgetIds);
    persistState(defaultLayout, initialWidgetIds);
  }, [persistState]);

  const widgetPropsById = useMemo(
    () => ({
      advanced_contact_list: {
        onEdit: setEditingContact,
      },
      customer_form: {
        onCustomerCreated: (customerId) => {
          setNewCustomerId(customerId);
          setShowContactModal(true);
        },
      },
      advanced_customer_list: {
        onEditCustomer: setEditingCustomer,
      },
      contact_form: {},
      job_list: {},
    }),
    []
  );

  const handleAddWidget = useCallback(
    (rawWidgetId) => {
      const widgetId = migrateWidgetId(rawWidgetId);

      if (!getSurfaceWidget(SURFACE, widgetId)) {
        alert("This widget is not available on the Clients page.");
        return;
      }

      if (widgetIds.includes(widgetId)) {
        alert("Widget already exists");
        return;
      }

      const bottomY = layout.length === 0
        ? 0
        : Math.max(...layout.map((item) => (item?.y || 0) + (item?.h || 1)));
      const newY = Math.max(0, bottomY);
      const newItem = createSurfaceLayoutItem(SURFACE, widgetId, { x: 0, y: newY });
      const nextLayout = [...layout, newItem];
      const nextWidgetIds = [...widgetIds, widgetId];

      setLayout(nextLayout);
      setWidgetIds(nextWidgetIds);
      persistState(nextLayout, nextWidgetIds);
      setShowAddModal(false);
    },
    [layout, persistState, widgetIds]
  );

  const handleRemoveWidget = useCallback(
    (widgetId) => {
      const nextWidgetIds = widgetIds.filter((id) => id !== widgetId);
      const nextLayout = layout.filter((item) => item.i !== widgetId);
      setWidgetIds(nextWidgetIds);
      setLayout(nextLayout);
      persistState(nextLayout, nextWidgetIds);
    },
    [layout, persistState, widgetIds]
  );

  const onLayoutChange = useCallback(
    (newLayout) => {
      const validIds = new Set(widgetIds);
      const normalised = normaliseLayout(
        newLayout.filter((item) => validIds.has(item.i))
      );
      setLayout(normalised);
      persistState(normalised, widgetIds);
    },
    [persistState, widgetIds]
  );

  /*
   * Mobile is a presentation of the existing Client Manager configuration,
   * not a second editable layout. Follow the saved desktop visual order
   * without persisting mobile positions back into clientsLayout.
   */
  const mobileWidgetIds = useMemo(() => {
    const positionById = new Map(
      layout.map((item, index) => [
        item.i,
        {
          y: Number.isFinite(item?.y) ? item.y : Number.MAX_SAFE_INTEGER,
          x: Number.isFinite(item?.x) ? item.x : Number.MAX_SAFE_INTEGER,
          index,
        },
      ])
    );

    return [...widgetIds].sort((a, b) => {
      const pa = positionById.get(a) || {
        y: Number.MAX_SAFE_INTEGER,
        x: Number.MAX_SAFE_INTEGER,
        index: widgetIds.indexOf(a),
      };

      const pb = positionById.get(b) || {
        y: Number.MAX_SAFE_INTEGER,
        x: Number.MAX_SAFE_INTEGER,
        index: widgetIds.indexOf(b),
      };

      if (pa.y !== pb.y) return pa.y - pb.y;
      if (pa.x !== pb.x) return pa.x - pb.x;
      return pa.index - pb.index;
    });
  }, [layout, widgetIds]);

  const getMobileWidgetHeight = useCallback(
    (widgetId) => {
      const item = layout.find((layoutItem) => layoutItem.i === widgetId);

      const h = Number.isFinite(item?.h)
        ? Math.max(1, item.h)
        : 4;

      return Math.max(
        280,
        h * rowHeight + Math.max(0, h - 1) * 24
      );
    },
    [layout, rowHeight]
  );

  return (
    <div
      className={isMobile ? undefined : "p-6"}
      style={{
        backgroundColor: "#f0f0f0",
        minHeight: isMobile ? "100%" : "100vh",
        overflowX: "hidden",
        paddingTop: isMobile ? "4px" : undefined,
        paddingBottom: isMobile ? "16px" : undefined,
        paddingLeft: isMobile ? "12px" : containerPadding,
        paddingRight: isMobile ? "12px" : containerPadding,
        boxSizing: "border-box",
      }}
    >
      <div ref={gridRef} className="w-full" style={{ minWidth: 0 }}>
        <div
          className={isMobile ? undefined : "flex justify-between items-center mb-1"}
          style={{
            minHeight: isMobile ? "58px" : headerHeight,
            height: isMobile ? "auto" : headerHeight,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: isMobile ? "12px" : undefined,
            marginBottom: isMobile ? "6px" : undefined,
          }}
        >
          <h2
            className={isMobile ? undefined : "text-2xl font-semibold"}
            style={{
              fontSize: isMobile ? "1.35rem" : undefined,
              fontWeight: 600,
              lineHeight: 1.2,
              margin: 0,
              minWidth: 0,
            }}
          >
            Client Manager
          </h2>
          <button
            type="button"
            onClick={() => setShowAddModal(true)}
            style={{
              backgroundColor: theme.button_action?.normal || "#000",
              color: "#fff",
              padding: isMobile ? "10px 14px" : "10px 18px",
              minHeight: isMobile ? "44px" : undefined,
              whiteSpace: "nowrap",
              borderRadius: "6px",
              fontWeight: "bold",
            }}
          >
            Add Widget
          </button>
        </div>

        {isMobile ? (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "12px",
              width: "100%",
              minWidth: 0,
              paddingBottom: "8px",
            }}
          >
            {mobileWidgetIds.map((widgetId) => (
              <div
                key={widgetId}
                style={{
                  width: "100%",
                  minWidth: 0,
                  height: `${getMobileWidgetHeight(widgetId)}px`,
                  overflow: "hidden",
                }}
              >
                <WidgetRenderer
                  widgetId={widgetId}
                  surface={SURFACE}
                  widgetProps={widgetPropsById[widgetId] || {}}
                  onRemove={() => handleRemoveWidget(widgetId)}
                />
              </div>
            ))}
          </div>
        ) : (
          <GridLayout
            className="layout"
            layout={layout}
            cols={12}
            rowHeight={rowHeight}
            width={gridWidth}
            draggableHandle=".widget-drag-handle"
            resizeHandles={["se"]}
            isResizable
            isDraggable
            onLayoutChange={onLayoutChange}
            compactType={null}
            preventCollision={false}
            margin={[16, 24]}
          >
            {widgetIds.map((widgetId) => (
              <div key={widgetId} style={{ height: "100%", minHeight: 0 }}>
                <WidgetRenderer
                  widgetId={widgetId}
                  surface={SURFACE}
                  widgetProps={widgetPropsById[widgetId] || {}}
                  onRemove={() => handleRemoveWidget(widgetId)}
                />
              </div>
            ))}
          </GridLayout>
        )}

        {showAddModal && (
          <AddWidgetModal
            surface={SURFACE}
            excludedWidgetIds={widgetIds}
            onClose={() => setShowAddModal(false)}
            onSelect={handleAddWidget}
          />
        )}

        {selectedUserId && (
          <ManageUserModal userId={selectedUserId} onClose={() => setSelectedUserId(null)} />
        )}
        {editingCustomer && (
          <EditCustomerModal customer={editingCustomer} onClose={() => setEditingCustomer(null)} />
        )}
        {editingContact && (
          <EditContactModal contact={editingContact} onClose={() => setEditingContact(null)} />
        )}
        {showContactModal && newCustomerId && (
          <CreateContactModal
            customerId={newCustomerId}
            onClose={() => {
              setShowContactModal(false);
              setNewCustomerId(null);
            }}
          />
        )}
      </div>
    </div>
  );
};

export default ClientsPage;
